import {
    FrameAugmentVisitor,
    IJsonPath
} from '@mintdata-wsp/common/controllers/spreadsheet/functions/util/frames/FrameAugmentVisitor';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {
    IFormulaExpression,
    ISheetAPI,
    ISheetLocation,
    ISheetRange,
    ISheetValue,
    NamedArguments,
    SheetType,
    SheetValueType,
    SimpleNamingContext
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {FieldName, FrameData, IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';
import * as jp from 'jsonpath/jsonpath.min';

type FrameOp<T> = (targetFrame:IFrame) => Promise<T | AError>;

type FrameFieldsObj = {[key:string]:boolean};

class Cardinality
{
    public static readonly ANY:Cardinality = new Cardinality(0, Number.MAX_VALUE);
    public static readonly EXACTLY_ONE:Cardinality = new Cardinality(1, 1);
    public static readonly AT_MOST_ONE:Cardinality = new Cardinality(0, 1);

    private constructor(public readonly minRows:number,
                        public readonly maxRows:number)
    {
    }
}

export class FramesFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'frames';
    }

    @Function({
        title: 'FRAME(value, namesOnFirstRow [, auto])',
        alias: 'FRAME',
        example: 'FRAME(A1:A2)',
        tip: 'Returns frame',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'result',
                tip: 'Resulting frame'
            }
    })
    @bind
    public frame(
        @Param({
            title: 'api',
            tip: 'api',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'value',
            tip: 'Value to build a new frame from',
            type: SheetType.Any
        }) value:any,
        @Param({
            title: 'namesOnFirstRow',
            tip: 'Column names on first row. Default is TRUE',
            optional: true,
            type: Type.Boolean
        }) namesOnFirstRow:ABoolean,
        @Param({
            title: 'auto',
            tip: 'The bottom boundary of the range is determined by an empty value in a first column. Default is FALSE',
            optional: true,
            type: Type.Boolean
        }) auto:ABoolean):IFrame | AError
    {
        const isNamesOnFirstRow = namesOnFirstRow == null || namesOnFirstRow.value;
        const bAuto = auto && auto.value;

        const frameOrError = this._getArgumentAsFrame(api, value, bAuto, false);
        if( Type.isInstanceOf(frameOrError, Type.Error) )
            return frameOrError as AError;

        const frame = frameOrError as IFrame;
        if( !isNamesOnFirstRow || frame.recordCount === 0 )
            return frame;

        // _makeFrame() mutates data
        const sourceFrameData = isNamesOnFirstRow ? frame.getData() : frame.getDataUnsafe();

        return this._makeFrame(sourceFrameData, isNamesOnFirstRow);
    }

    @Function({
        title: 'COLUMN_OF(value, times [, column])',
        alias: 'COLUMN_OF',
        example: 'COLUMN_OF(A1, 10)',
        tip: 'Returns a frame with 1 column, specified rows count, where provided value is set into each cell',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'result',
                tip: 'Resulting frame'
            }
    })
    @bind
    public columnOf(
        @Param({
            title: 'api',
            tip: 'api',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'value',
            tip: 'Value to fill the frame',
            type: SheetType.AnyIValue
        }) value:IValue,
        @Param({
            title: 'times',
            tip: 'Rows count',
            type: Type.Integer
        }) times:AInteger,
        @Param({
            title: 'column',
            tip: 'The name of column',
            optional: true,
            type: Type.String
        }) column:AString):IFrame | AError
    {
        if( times.isError || (column && column.isError) )
            return new AError('#INVALID_ARGUMENT');

        const columnName:string = column ? column.value : 'column1';

        return Frames.fromFieldsAndData(
            [new FrameField(columnName, value.getType())],
            new Array(times.value).fill([value])
        );
    }

    @Function({
        title: 'FLIP_FRAME(value, namesOnFirstColumn [, auto])',
        alias: 'FLIP_FRAME',
        example: 'FLIP_FRAME(A1:A2)',
        tip: 'Returns flipped frame',
        icon: '',
        returns:
            {
                type: Type.Frame,
                title: 'result',
                tip: 'Resulting frame'
            }
    })
    @bind
    public flipFrame(
        @Param({
            title: 'api',
            tip: 'api',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'value',
            tip: 'value to flip',
            type: SheetType.Any
        }) value:any,
        @Param({
            title: 'namesOnFirstColumn',
            tip: 'Column names on first column. Default is TRUE',
            optional: true,
            type: Type.Boolean
        }) hasHeaders:ABoolean,
        @Param({
            title: 'auto',
            tip: 'The right boundary of the range is determined by an empty value in a first row. Default is FALSE. Only used, when passing a range as first parameter.',
            optional: true,
            type: Type.Boolean
        }) auto:ABoolean):IFrame | AError
    {
        const bHasHeaders = hasHeaders == null || hasHeaders.value;
        const bAuto = auto && auto.value;

        const frameOrError = this._getArgumentAsFrame(api, value, false, bAuto);
        if( Type.isInstanceOf(frameOrError, Type.Error) )
            return frameOrError as AError;

        const frame = frameOrError as IFrame;
        const data = frame.getDataUnsafe();

        const newRowCount = frame.fieldCount;
        const newColCount = frame.recordCount;

        // do the flip
        const flipped = [];
        for( let rowIndex = 0; rowIndex < newRowCount; ++rowIndex )
        {
            const row = [];
            for( let colIndex = 0; colIndex < newColCount; ++colIndex )
                row[colIndex] = data[colIndex][rowIndex];
            flipped.push(row);
        }

        return this._makeFrame(flipped, bHasHeaders);
    }

    @Function({
        title: 'ADD_FIRST_ROW(targetFrame, frameToAdd [, addAllRows = FALSE])',
        tip: 'This function inserts at row_index == 0, causing all other rows to shift down by one.',
        alias: 'ADD_FIRST_ROW',
        example: 'ADD_FIRST_ROW(A1, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async addFirstRow(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Frame/range to add',
            tip: 'Frame/range to add',
            type: Type.Frame
        }) frameToAdd:IFrame,
        @Param({
            title: 'addAll',
            tip: 'Add all rows?',
            type: Type.Boolean,
            optional: true
        }) addAll:ABoolean):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
            api.getSheetContext().insertFrameRows(targetLocation, 0, frameToAdd, addAll && addAll.value)
        );
    }

    @Function({
        title: 'ADD_ROW(targetFrame, frameToAdd [, addAllRows = FALSE])',
        tip: 'Inserts row to the end of the frame.‌',
        alias: 'ADD_ROW',
        example: 'ADD_ROW(A1, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async addRow(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Frame/range to add',
            tip: 'Frame/range to add',
            type: Type.Frame
        }) frameToAdd:IFrame,
        @Param({
            title: 'addAll',
            tip: 'Add all rows?',
            type: Type.Boolean,
            optional: true
        }) addAll:ABoolean):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
            api.getSheetContext().insertFrameRows(targetLocation, frame.recordCount, frameToAdd, addAll && addAll.value)
        );
    }

    @Function({
        title: 'ADD_ROW_AFTER(targetFrame, searchExpr, frameOrRangeToAdd [, addAllRows = FALSE])',
        tip: 'Uses searchExpr on TargetFrame to select a set of row(s) in the TargetFrame, ' +
            'and then inserts the frameOrRangeToAdd after the selected row(s) returned by expression.‌',
        alias: 'ADD_ROW_AFTER',
        example: 'ADD_ROW_AFTER(A1, @id = 5, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async addRowAfter(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Search Expression',
            tip: 'Search Expression',
            type: SheetType.FormulaExpression
        }) searchExpr:IFormulaExpression,
        @Param({
            title: 'Frame/range to add',
            tip: 'Frame/range to add',
            type: Type.Frame
        }) frameToAdd:IFrame,
        @Param({
            title: 'addAll',
            tip: 'Add all rows?',
            type: Type.Boolean,
            optional: true
        }) addAll:ABoolean):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const indexesOrError = await this._searchFrameRowsByExprFormula(api, frame, searchExpr, Cardinality.EXACTLY_ONE);
            if( Type.isInstanceOf(indexesOrError, Type.Error) )
                return indexesOrError as AError;

            const insertIndex = (indexesOrError as Array<number>)[0] + 1;
            return api.getSheetContext()
                .insertFrameRows(targetLocation, insertIndex, frameToAdd, addAll && addAll.value);
        });
    }

    @Function({
        title: 'ADD_ROW_BEFORE(targetFrame, searchExpr, frameOrRangeToAdd [, addAllRows = FALSE])',
        tip: 'Uses searchExpr on TargetFrame to select a set of row(s) in the TargetFrame, ' +
            'and then inserts the frameOrRangeToAdd before the selected row(s) returned by expression.‌',
        alias: 'ADD_ROW_BEFORE',
        example: 'ADD_ROW_BEFORE(A1, @id = 5, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async addRowBefore(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Search Expression',
            tip: 'Search Expression',
            type: SheetType.FormulaExpression
        }) searchExpr:IFormulaExpression,
        @Param({
            title: 'Frame/range to add',
            tip: 'Frame/range to add',
            type: Type.Frame
        }) frameToAdd:IFrame,
        @Param({
            title: 'addAll',
            tip: 'Add all rows?',
            type: Type.Boolean,
            optional: true
        }) addAll:ABoolean):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const indexesOrError = await this._searchFrameRowsByExprFormula(api, frame, searchExpr, Cardinality.EXACTLY_ONE);
            if( Type.isInstanceOf(indexesOrError, Type.Error) )
                return indexesOrError as AError;

            const insertIndex = (indexesOrError as Array<number>)[0];
            return api.getSheetContext()
                .insertFrameRows(targetLocation, insertIndex, frameToAdd, addAll && addAll.value);
        });
    }

    @Function({
        title: 'UPDATE_FIRST_ROW(targetFrame, frameOrRangeToUpdateWith)',
        tip: 'Updates the first row in an existing frame.',
        alias: 'UPDATE_FIRST_ROW',
        example: 'UPDATE_FIRST_ROW(A1, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async updateFirstRow(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Frame/range to update with',
            tip: 'Frame/range to update with',
            type: Type.Frame
        }) updateFrame:IFrame):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            if( frame.recordCount == 0 )
                return new AError('#CANT_UPDATE_EMPTY_FRAME');

            return api.getSheetContext().updateFrameRow(targetLocation, 0, updateFrame);
        });
    }

    @Function({
        title: 'UPDATE_LAST_ROW(targetFrame, frameOrRangeToUpdateWith)',
        tip: 'Updates the last row in an existing frame.',
        alias: 'UPDATE_LAST_ROW',
        example: 'UPDATE_LAST_ROW(A1, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async updateLastRow(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Frame/range to update with',
            tip: 'Frame/range to update with',
            type: Type.Frame
        }) updateFrame:IFrame):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            if( frame.recordCount == 0 )
                return new AError('#CANT_UPDATE_EMPTY_FRAME');

            return api.getSheetContext().updateFrameRow(targetLocation, frame.recordCount - 1, updateFrame);
        });
    }

    @Function({
        title: 'UPDATE_ROW(targetFrame, searchExpr, frameOrRangeToUpdateWith)',
        tip: 'Updates a row in an existing frame. Uses searchExpr to select a single row in TargetFrame.',
        alias: 'UPDATE_ROW',
        example: 'UPDATE_ROW(A1, @id = 5, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async updateRow(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Search Expression',
            tip: 'Search Expression',
            type: SheetType.FormulaExpression
        }) searchExpr:IFormulaExpression,
        @Param({
            title: 'Frame/range to update with',
            tip: 'Frame/range to update with',
            type: Type.Frame
        }) updateFrame:IFrame):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const indexesOrError = await this._searchFrameRowsByExprFormula(api, frame, searchExpr, Cardinality.EXACTLY_ONE);
            if( Type.isInstanceOf(indexesOrError, Type.Error) )
                return indexesOrError as AError;

            const updateIndex = (indexesOrError as Array<number>)[0];
            return api.getSheetContext().updateFrameRow(targetLocation, updateIndex, updateFrame);
        });
    }

    @Function({
        title: 'UPSERT_ROW(targetFrame, searchExpr, frameOrRangeToUpsert)',
        tip: 'Updates a row or inserts a new row (after last row in TargetFrame).',
        alias: 'UPSERT_ROW',
        example: 'UPSERT_ROW(A1, @id = 5, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async upsertRow(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Search Expression',
            tip: 'Search Expression',
            type: SheetType.FormulaExpression
        }) searchExpr:IFormulaExpression,
        @Param({
            title: 'Frame/range to upsert',
            tip: 'Frame/range to upsert',
            type: Type.Frame
        }) upsertFrame:IFrame):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const indexesOrError = await this._searchFrameRowsByExprFormula(api, frame, searchExpr, Cardinality.AT_MOST_ONE);
            if( Type.isInstanceOf(indexesOrError, Type.Error) )
                return indexesOrError as AError;

            // below code works even with empty array
            const updateIndex = (indexesOrError as Array<number>)[0];
            if( updateIndex !== undefined )
                // record found => execute update
                return api.getSheetContext().updateFrameRow(targetLocation, updateIndex, upsertFrame);
            else
                // otherwise insert new record to the end
                return api.getSheetContext().insertFrameRows(targetLocation, frame.recordCount, upsertFrame, false);
        });
    }

    @Function({
        title: 'DELETE_ROW(targetFrame, searchExpr [, keepBindings = TRUE])',
        tip: 'Deletes a row from the frame.',
        alias: 'DELETE_ROW',
        example: 'DELETE_ROW(A1, @id = 5)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if rows was deleted, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async deleteRow(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Search Expression',
            tip: 'Search Expression',
            type: SheetType.FormulaExpression
        }) searchExpr:IFormulaExpression,
        @Param({
            title: 'Keep bindings?',
            tip: 'Keep bindings?',
            type: Type.Boolean,
            optional: true
        }) keepBindings:ABoolean):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const indexesOrError = await this._searchFrameRowsByExprFormula(api, frame, searchExpr, Cardinality.AT_MOST_ONE);
            if( Type.isInstanceOf(indexesOrError, Type.Error) )
                return indexesOrError as AError;

            // below code works even with empty array
            const deleteIndex = (indexesOrError as Array<number>)[0];
            if( deleteIndex === undefined )
                return null;

            return api.getSheetContext().deleteFrameRows(
                targetLocation,
                [deleteIndex],
                !keepBindings || keepBindings.value
            );
        });
    }

    @Function({
        title: 'DELETE_ROWS(targetFrame, searchExpr [, keepBindings = TRUE])',
        tip: 'Deletes a row from the frame.',
        alias: 'DELETE_ROWS',
        example: 'DELETE_ROWS(A1, @id = 5)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns number of rows deleted.',
                type: Type.Integer
            }
    })
    @bind
    public async deleteRows(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Search Expression',
            tip: 'Search Expression',
            type: SheetType.FormulaExpression
        }) searchExpr:IFormulaExpression,
        @Param({
            title: 'Keep bindings?',
            tip: 'Keep bindings?',
            type: Type.Boolean,
            optional: true
        }) keepBindings:ABoolean):Promise<AInteger | AError>
    {
        let deletedCount;

        const opResult = await this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const indexesOrError = await this._searchFrameRowsByExprFormula(api, frame, searchExpr, Cardinality.ANY);
            if( Type.isInstanceOf(indexesOrError, Type.Error) )
                return indexesOrError as AError;

            const deleteIndexes = (indexesOrError as Array<number>);
            deletedCount = deleteIndexes.length;

            if( deletedCount === 0 )
                return null;

            return api.getSheetContext().deleteFrameRows(
                targetLocation,
                deleteIndexes,
                !keepBindings || keepBindings.value
            );
        });

        if( Type.isInstanceOf(opResult, Type.Error) )
            return opResult as AError;

        return AInteger.fromNumber(deletedCount);
    }

    @Function({
        title: 'DELETE_ROW_AT(targetFrame, rowNumber)',
        tip: 'Deletes a row with rowNumber from the frame.',
        alias: 'DELETE_ROW_AT',
        example: 'DELETE_ROW_AT(A1, 2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if rows was deleted, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async deleteRowAt(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Row Number',
            tip: 'Row Number',
            type: Type.Integer
        }) rowNumber:AInteger,
        @Param({
            title: 'Keep bindings?',
            tip: 'Keep bindings?',
            type: Type.Boolean,
            optional: true
        }) keepBindings:ABoolean):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const searchExpr:string = `${!frame.findField('row') ? '@row' : '@\'_row\''} = ${rowNumber.value}`;
            const indexesOrError = await this._searchFrameRowsByExprString(api, frame, searchExpr, Cardinality.AT_MOST_ONE);

            if( Type.isInstanceOf(indexesOrError, Type.Error) )
                return indexesOrError as AError;

            // below code works even with empty array
            const deleteIndex = (indexesOrError as Array<number>)[0];
            if( deleteIndex === undefined )
                return null;

            return api.getSheetContext().deleteFrameRows(
                targetLocation,
                [deleteIndex],
                !keepBindings || keepBindings.value
            );
        });
    }

    @Function({
        title: 'ADD_COLUMN(targetFrame, columnName [, columnType = string])',
        tip: 'Adds a column to TargetFrame at the very end, named ColumnName of type ColumnType.',
        alias: 'ADD_COLUMN',
        example: 'ADD_COLUMN(A1, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async addColumn(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Column Name',
            tip: 'Column Name',
            type: Type.String
        }) columnName:AString,
        @Param({
            title: 'Column Type',
            tip: 'Column Type',
            type: Type.String,
            optional: true
        }) columnType:AString):Promise<ABoolean | AError>
    {
        const type = Type.fromTypeName(columnType && columnType.value || 'string');
        if( !type )
            return new AError('#INVALID_TYPE_NAME');

        return this._withFrameAtLocation(api, targetLocation, async frame =>
            api.getSheetContext().insertFrameColumns(targetLocation, frame.fieldCount, columnName.value, type)
        );
    }

    @Function({
        title: 'ADD_COLUMN_AFTER(targetFrame, targetColumnName|targetColumnOffset, columnNameToAdd [, columnType = string])',
        tip: 'Adds a column to TargetFrame after a TargetColumnName, named ColumnName of type ColumnType.',
        alias: 'ADD_COLUMN_AFTER',
        example: 'ADD_COLUMN_AFTER(A1, A2, A3)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async addColumnAfter(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Target Column Name or Column Offset',
            tip: 'Target Column Name or Column Offset',
            type: SheetType.AnyIValue,
        }) targetColumnNameOrOffset:IValue,
        @Param({
            title: 'Column Name to Add',
            tip: 'Column Name to Add',
            type: Type.String
        }) columnName:AString,
        @Param({
            title: 'Column Type',
            tip: 'Column Type',
            type: Type.String,
            optional: true
        }) columnType:AString):Promise<ABoolean | AError>
    {
        const type = Type.fromTypeName(columnType && columnType.value || 'string');
        if( !type )
            return new AError('#INVALID_TYPE_NAME');

        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const searchIndexOrError = this._searchFrameColumn(targetColumnNameOrOffset, frame);
            if( Type.isInstanceOf(searchIndexOrError, Type.Error) )
                return searchIndexOrError as AError;

            const searchIndex = searchIndexOrError as number;
            return api.getSheetContext().insertFrameColumns(targetLocation, searchIndex + 1, columnName.value, type);
        });
    }

    @Function({
        title: 'ADD_COLUMN_BEFORE(targetFrame, targetColumnName|targetColumnOffset, columnNameToAdd [, columnType = string])',
        tip: 'Adds a column to TargetFrame before a TargetColumnName, named ColumnName of type ColumnType.',
        alias: 'ADD_COLUMN_BEFORE',
        example: 'ADD_COLUMN_BEFORE(A1, A2, A3)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async addColumnBefore(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Target Column Name or Column Offset',
            tip: 'Target Column Name or Column Offset',
            type: SheetType.AnyIValue,
        }) targetColumnNameOrOffset:IValue,
        @Param({
            title: 'Column Name to Add',
            tip: 'Column Name to Add',
            type: Type.String
        }) columnName:AString,
        @Param({
            title: 'Column Type',
            tip: 'Column Type',
            type: Type.String,
            optional: true
        }) columnType:AString):Promise<ABoolean | AError>
    {
        const type = Type.fromTypeName(columnType && columnType.value || 'string');
        if( !type )
            return new AError('#INVALID_TYPE_NAME');

        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const searchIndexOrError = this._searchFrameColumn(targetColumnNameOrOffset, frame);
            if( Type.isInstanceOf(searchIndexOrError, Type.Error) )
                return searchIndexOrError as AError;

            const searchIndex = searchIndexOrError as number;
            return api.getSheetContext().insertFrameColumns(targetLocation, searchIndex, columnName.value, type);
        });
    }

    @Function({
        title: 'UPDATE_COLUMN(targetFrame, oldColumnName, newColumnName [, newColumnType = null])',
        tip: 'Updates a column. If a NewColumnType is specified (& differs from the current column type), ' +
            'then all cells in the column are put through regular type coercion in addition to the column name being changed.',
        alias: 'UPDATE_COLUMN',
        example: 'UPDATE_COLUMN(A1, A2, A3)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async updateColumn(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Old Column Name',
            tip: 'Column Name to Update',
            type: Type.String
        }) oldColumnName:AString,
        @Param({
            title: 'New Column Name',
            tip: 'New Column Name',
            type: Type.String
        }) newColumnName:AString,
        @Param({
            title: 'Column Type',
            tip: 'Column Type',
            type: Type.String,
            optional: true
        }) newColumnType:AString):Promise<ABoolean | AError>
    {
        let newType = null;
        if( newColumnType )
        {
            newType = Type.fromTypeName(newColumnType.value);
            if( !newType )
                return new AError('#INVALID_TYPE_NAME');
        }

        // noop case
        if( newColumnName.value === oldColumnName.value )
            return ABoolean.true();

        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            if( frame.findField(newColumnName.value) )
                return new AError('#COLUMN_NAMES_NOT_UNIQUE');

            const searchIndexOrError = this._searchFrameColumn(oldColumnName, frame);
            if( Type.isInstanceOf(searchIndexOrError, Type.Error) )
                return searchIndexOrError as AError;

            const searchIndex = searchIndexOrError as number;
            return api.getSheetContext().updateFrameColumn(targetLocation, searchIndex, newColumnName.value, newType);
        });
    }

    @Function({
        title: 'DELETE_COLUMN(targetFrame, columnName)',
        tip: 'Deletes a column from TargetFrame.',
        alias: 'DELETE_COLUMN',
        example: 'DELETE_COLUMN(A1, A2)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async deleteColumn(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Column Name',
            tip: 'Column Name',
            type: Type.String
        }) columnName:AString):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const searchIndexOrError = this._searchFrameColumn(columnName, frame);
            if( Type.isInstanceOf(searchIndexOrError, Type.Error) )
                return searchIndexOrError as AError;

            const searchIndex = searchIndexOrError as number;
            return api.getSheetContext().deleteFrameColumns(targetLocation, [searchIndex]);
        });
    }

    @Function({
        title: 'DELETE_COLUMNS(targetFrame, columnName [, columnName2, columnName3, ...] )',
        tip: 'Deletes multiple columns from TargetFrame.',
        alias: 'DELETE_COLUMNS',
        example: 'DELETE_COLUMNS(A1, A2, A3, A4, ...)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async deleteColumns(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Column Names',
            tip: 'Column Names',
            type: Type.String,
            repeatable: true
        }) columnNames:AString[]):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            const deleteIndexes = [];
            for( let columnName of columnNames )
            {
                const searchIndexOrError = this._searchFrameColumn(columnName, frame);
                if( Type.isInstanceOf(searchIndexOrError, Type.Error) )
                    return searchIndexOrError as AError;

                deleteIndexes.push(searchIndexOrError as number);
            }

            return api.getSheetContext().deleteFrameColumns(targetLocation, deleteIndexes);
        });
    }

    @Function({
        title: 'HAS_COLUMN(targetFrame, columnName)',
        tip: 'Check for column name in TargetFrame.',
        alias: 'HAS_COLUMN',
        example: 'HAS_COLUMN(A1, A2), HAS_COLUMN(A1, "column-name")',
        icon: '',
        lazyOnly: false,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public hasColumn(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: Type.Frame
        }) targetFrame:IFrame,
        @Param({
            title: 'Column Name',
            tip: 'Column Name',
            type: Type.String
        }) columnName:AString):ABoolean | AError
    {
        if( targetFrame.isEmpty )
            return ABoolean.false();

        const frameFields:FrameFieldsObj = this._getFrameFields(targetFrame);

        if( !frameFields[(columnName as AString).value.toLowerCase()] )
            return ABoolean.false();

        return ABoolean.true();
    }

    @Function({
        title: 'HAS_COLUMNS(targetFrame, columnName)',
        tip: 'Check for column names in TargetFrame.',
        alias: 'HAS_COLUMNS',
        example: 'HAS_COLUMNS(A1, A2, "a", {"b", "c"})',
        icon: '',
        lazyOnly: false,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public hasColumns(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: Type.Frame
        }) targetFrame:IFrame,
        @Param({
            title: 'Column Name',
            tip: 'Column Name',
            type: SheetType.AnyIValue,
            repeatable: true
        }) columnNames:IValue[]):ABoolean | AError
    {
        if( targetFrame.isEmpty )
            return ABoolean.false();

        let frameFields:FrameFieldsObj = null;

        const fieldNotFound = (columnName:IValue):boolean =>
        {
            if( !Type.isInstanceOf(columnName, Type.String) )
            {
                columnName = CoercionUtils.coerce(columnName, Type.String, null);
                if( Type.isInstanceOf(columnName, Type.Error) )
                    return false;
            }

            if( !frameFields )
                frameFields = this._getFrameFields(targetFrame);

            return !frameFields[(columnName as AString).value.toLowerCase()];
        };

        for( let columnName of columnNames )
        {
            if( Type.isInstanceOf(columnName, Type.Frame) )
            {
                const names:IValue[] = (columnName as IFrame).getAllValues();

                for( let columnName of names )
                {
                    if( !Type.isInstanceOf(columnName, Type.Frame) && fieldNotFound(columnName) )
                        return ABoolean.false();
                }
            }
            else
            {
                if( fieldNotFound(columnName) )
                    return ABoolean.false();
            }
        }
        return ABoolean.true();
    }

    @Function({
        title: 'SET_FRAME_CELL(targetFrame, columnName, searchExpression, newValue)',
        tip: 'This function finds a column named ColumnName, and then any row(s) matching Expr, and sets them to the value in NewValue.',
        alias: 'SET_FRAME_CELL',
        example: 'SET_FRAME_CELL(A1, A2, @field = A3, A4)',
        icon: '',
        lazyOnly: true,
        returns:
            {
                title: 'value',
                tip: 'Returns TRUE if successful, FALSE otherwise.',
                type: Type.Boolean
            }
    })
    @bind
    public async setFrameCell(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Column Name',
            tip: 'Column Name',
            type: Type.String,
            optional: true
        }) columnName:AString,
        @Param({
            title: 'Search Expression',
            tip: 'Search Expression',
            type: SheetType.FormulaExpression
        }) searchExpr:IFormulaExpression,
        @Param({
            title: 'New Value',
            tip: 'New Value',
            type: SheetType.AnyIValue
        }) newValue:IValue):Promise<ABoolean | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async frame =>
        {
            let sColumnName = null;
            if( columnName && Type.isInstanceOf(columnName, Type.String) )
            {
                if( !frame.findField(columnName.value) )
                    return new AError('#COLUMN_NOT_FOUND');
                sColumnName = columnName.value;
            }

            const setResult = await this._setCellsValues(api, frame, sColumnName, searchExpr, newValue);

            if (Type.isInstanceOf(setResult, Type.Error)) {
                return setResult as AError;
            }

            api.getSheetContext().setCellFormula(targetLocation, setResult);

            return ABoolean.true();
        });
    }

    @Function({
        title: 'filter(targetFrame, searchExpr)',
        tip: 'Returns a filtered version of the source frame, returning only rows or columns which meet the specified conditions.',
        alias: 'FILTER',
        example: 'FILTER(A1, @field > 10)',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Cell value or frame',
                tip: 'Returns value or frame extracted by EL pattern',
            },
    })
    @bind
    public async filter(@Param({
                            title: 'sheetAPI',
                            tip: 'API',
                            type: SheetType.SheetAPI
                        }) api:ISheetAPI,
                        @Param({
                            title: 'inputFrame',
                            tip: 'The data to be filtered.',
                            type: Type.Frame
                        }) targetFrame:IFrame,
                        @Param({
                            title: 'Search Expression',
                            tip: 'Search Expression',
                            type: SheetType.FormulaExpression
                        }) searchExpr:IFormulaExpression):Promise<IValue | AError>
    {
        if( targetFrame.isEmpty )
            return targetFrame;

        const indexesOrError:Array<number> | AError = await this._searchFrameRowsByExprFormula(api, targetFrame, searchExpr, Cardinality.ANY);
        if( Type.isInstanceOf(indexesOrError, Type.Error) )
            return indexesOrError as AError;

        const targetData = targetFrame.getDataUnsafe();
        const filteredData = (indexesOrError as Array<number>).map(index =>
            targetData[index].slice()
        );

        return Frames.fromFieldsAndData(targetFrame.getFields(), filteredData);
    }

    @Function({
        title: 'AUG(targetFrame, pathExpression, newValue)',
        tip: '',
        alias: 'AUG',
        example: 'AUG(A1, A2, A3)',
        icon: '',
        returns:
            {
                title: 'value',
                tip: 'Returns new frame with modifications.',
                type: Type.Boolean
            }
    })
    @bind
    public async augmentFrame(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Target Frame',
            tip: 'Target Frame',
            type: SheetType.SheetLocation
        }) targetLocation:ISheetLocation,
        @Param({
            title: 'Path Expression',
            tip: 'Path Expression',
            type: Type.String
        }) pathExpr:AString,
        @Param({
            title: 'New Value or Expression',
            tip: 'New Value or Expression',
            type: SheetType.FormulaExpression
        }) valueExpr:IFormulaExpression):Promise<IFrame | AError>
    {
        return this._withFrameAtLocation(api, targetLocation, async (frame:IFrame) =>
        {
            let jsonPaths:Array<IJsonPath> = jp.parse(pathExpr.value);

            if( jsonPaths.length === 0 )
                return frame;

            if( jsonPaths.length > 0 )
                jsonPaths.shift();

            const frameVisitor:FrameAugmentVisitor = new FrameAugmentVisitor(
                api,
                jsonPaths,
                valueExpr,
                new Map<string, IValue>()
            );
            await frame.acceptAsync(frameVisitor);

            const newFrame:IFrame = frameVisitor.getResult();

            return newFrame;
        });
    }

    @Function({
        title: 'JOIN_FRAMES(frame1, joinColumnName1, frame2, joinColumnName2, frame1Alias, frame2Alias)',
        tip: 'Combines rows from two frames based on a related column between them and returns a frame. Frame aliases are used as prefixes for columns in case of name collision.',
        alias: 'JOIN_FRAMES',
        example: 'JOIN_FRAMES(A1, "id", A2, "customer_id", "customers", "orders")',
        icon: '',
        lazyOnly: false,
        returns:
            {
                title: 'result',
                tip: 'Resulting frame',
                type: Type.Frame
            }
    })
    @bind
    public joinFrames(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Frame1',
            tip: 'Frame1',
            type: Type.Frame
        }) frame1:IFrame,
        @Param({
            title: 'Frame1 Join Column Name',
            tip: 'Frame1 Join Column Name',
            type: Type.String,
        }) joinColumnName1:AString,
        @Param({
            title: 'Frame2',
            tip: 'Frame2',
            type: Type.Frame
        }) frame2:IFrame,
        @Param({
            title: 'Frame2 Join Column Name',
            tip: 'Frame2 Join Column Name',
            type: Type.String,
            optional: true
        }) joinColumnName2:AString,
        @Param({
            title: 'Frame1 Alias',
            tip: 'Frame1 Alias',
            type: Type.String,
            optional: true
        }) frame1Alias:AString,
        @Param({
            title: 'Frame2 Alias',
            tip: 'Frame2 Alias',
            type: Type.String,
            optional: true
        }) frame2Alias:AString):IFrame | AError
    {
        return Frames.join(
            frame1,
            joinColumnName1.value,
            frame2,
            joinColumnName2 ? joinColumnName2.value : undefined,
            frame1Alias ? frame1Alias.value : undefined,
            frame2Alias ? frame2Alias.value : undefined,
        );
    }

    @Function({
        title: 'JOIN_FRAMES_LEFT(frame1, joinColumnName1, frame2, joinColumnName2, frame1Alias, frame2Alias)',
        tip: 'Combines rows from two frames based on a related column between them and returns a frame. Frame aliases are used as prefixes for columns in case of name collision.',
        alias: 'JOIN_FRAMES_LEFT',
        example: 'JOIN_FRAMES_LEFT(A1, "id", A2, "customer_id", "customers", "orders")',
        icon: '',
        lazyOnly: false,
        returns:
            {
                title: 'result',
                tip: 'Resulting frame',
                type: Type.Frame
            }
    })
    @bind
    public joinFramesLeft(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Frame1',
            tip: 'Frame1',
            type: Type.Frame
        }) frame1:IFrame,
        @Param({
            title: 'Frame1 Join Column Name',
            tip: 'Frame1 Join Column Name',
            type: Type.String,
        }) joinColumnName1:AString,
        @Param({
            title: 'Frame2',
            tip: 'Frame2',
            type: Type.Frame
        }) frame2:IFrame,
        @Param({
            title: 'Frame2 Join Column Name',
            tip: 'Frame2 Join Column Name',
            type: Type.String,
            optional: true
        }) joinColumnName2:AString,
        @Param({
            title: 'Frame1 Alias',
            tip: 'Frame1 Alias',
            type: Type.String,
            optional: true
        }) frame1Alias:AString,
        @Param({
            title: 'Frame2 Alias',
            tip: 'Frame2 Alias',
            type: Type.String,
            optional: true
        }) frame2Alias:AString):IFrame | AError
    {
        return Frames.joinLeft(
            frame1,
            joinColumnName1.value,
            frame2,
            joinColumnName2 ? joinColumnName2.value : undefined,
            frame1Alias ? frame1Alias.value : undefined,
            frame2Alias ? frame2Alias.value : undefined,
        );
    }

    @Function({
        title: 'JOIN_FRAMES_RIGHT(frame1, joinColumnName1, frame2, joinColumnName2, frame1Alias, frame2Alias)',
        tip: 'Combines rows from two frames based on a related column between them and returns a frame. Frame aliases are used as prefixes for columns in case of name collision.',
        alias: 'JOIN_FRAMES_RIGHT',
        example: 'JOIN_FRAMES_RIGHT(A1, "id", A2, "customer_id", "customers", "orders")',
        icon: '',
        lazyOnly: false,
        returns:
            {
                title: 'result',
                tip: 'Resulting frame',
                type: Type.Frame
            }
    })
    @bind
    public joinFramesRight(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Frame1',
            tip: 'Frame1',
            type: Type.Frame
        }) frame1:IFrame,
        @Param({
            title: 'Frame1 Join Column Name',
            tip: 'Frame1 Join Column Name',
            type: Type.String,
        }) joinColumnName1:AString,
        @Param({
            title: 'Frame2',
            tip: 'Frame2',
            type: Type.Frame
        }) frame2:IFrame,
        @Param({
            title: 'Frame2 Join Column Name',
            tip: 'Frame2 Join Column Name',
            type: Type.String,
            optional: true
        }) joinColumnName2:AString,
        @Param({
            title: 'Frame1 Alias',
            tip: 'Frame1 Alias',
            type: Type.String,
            optional: true
        }) frame1Alias:AString,
        @Param({
            title: 'Frame2 Alias',
            tip: 'Frame2 Alias',
            type: Type.String,
            optional: true
        }) frame2Alias:AString):IFrame | AError
    {
        return Frames.joinRight(
            frame1,
            joinColumnName1.value,
            frame2,
            joinColumnName2 ? joinColumnName2.value : undefined,
            frame1Alias ? frame1Alias.value : undefined,
            frame2Alias ? frame2Alias.value : undefined,
        );
    }

    @Function({
        title: 'JOIN_FRAMES_FULL(frame1, joinColumnName1, frame2, joinColumnName2, frame1Alias, frame2Alias)',
        tip: 'Combines rows from two frames based on a related column between them and returns a frame. Frame aliases are used as prefixes for columns in case of name collision.',
        alias: 'JOIN_FRAMES_FULL',
        example: 'JOIN_FRAMES_FULL(A1, "id", A2, "customer_id", "customers", "orders")',
        icon: '',
        lazyOnly: false,
        returns:
            {
                title: 'result',
                tip: 'Resulting frame',
                type: Type.Frame
            }
    })
    @bind
    public joinFramesFull(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Frame1',
            tip: 'Frame1',
            type: Type.Frame
        }) frame1:IFrame,
        @Param({
            title: 'Frame1 Join Column Name',
            tip: 'Frame1 Join Column Name',
            type: Type.String,
        }) joinColumnName1:AString,
        @Param({
            title: 'Frame2',
            tip: 'Frame2',
            type: Type.Frame
        }) frame2:IFrame,
        @Param({
            title: 'Frame2 Join Column Name',
            tip: 'Frame2 Join Column Name',
            type: Type.String,
            optional: true
        }) joinColumnName2:AString,
        @Param({
            title: 'Frame1 Alias',
            tip: 'Frame1 Alias',
            type: Type.String,
            optional: true
        }) frame1Alias:AString,
        @Param({
            title: 'Frame2 Alias',
            tip: 'Frame2 Alias',
            type: Type.String,
            optional: true
        }) frame2Alias:AString):IFrame | AError
    {
        return Frames.joinFull(
            frame1,
            joinColumnName1.value,
            frame2,
            joinColumnName2 ? joinColumnName2.value : undefined,
            frame1Alias ? frame1Alias.value : undefined,
            frame2Alias ? frame2Alias.value : undefined,
        );
    }

    @Function({
        title: 'JOIN_FRAMES_CROSS_PRODUCT(frame1, frame2, frame1Alias, frame2Alias)',
        tip: 'Combines rows from two frames based on their cross product - i.e. matches every record from frame1 with every record from frame2. Frame aliases are used as prefixes for columns in case of name collision.',
        alias: 'JOIN_FRAMES_CROSS_PRODUCT',
        example: 'JOIN_FRAMES_CROSS_PRODUCT(A1, A2, "customers", "orders")',
        icon: '',
        lazyOnly: false,
        returns:
            {
                title: 'result',
                tip: 'Resulting frame',
                type: Type.Frame
            }
    })
    @bind
    public joinFramesCrossProduct(
        @Param({
            title: 'sheetAPI',
            tip: 'API',
            type: SheetType.SheetAPI
        }) api:ISheetAPI,
        @Param({
            title: 'Frame1',
            tip: 'Frame1',
            type: Type.Frame
        }) frame1:IFrame,
        @Param({
            title: 'Frame2',
            tip: 'Frame2',
            type: Type.Frame
        }) frame2:IFrame,
        @Param({
            title: 'Frame1 Alias',
            tip: 'Frame1 Alias',
            type: Type.String,
            optional: true
        }) frame1Alias:AString,
        @Param({
            title: 'Frame2 Alias',
            tip: 'Frame2 Alias',
            type: Type.String,
            optional: true
        }) frame2Alias:AString):IFrame | AError
    {
        return Frames.joinCross(
            frame1,
            frame2,
            frame1Alias ? frame1Alias.value : undefined,
            frame2Alias ? frame2Alias.value : undefined,
        );
    }

    private async _withFrameAtLocation<T>(api:ISheetAPI, targetLocation:ISheetLocation, frameOp:FrameOp<T>):Promise<T | AError>
    {
        const targetValue = api.getSheetContext().getCellValue(targetLocation);
        if( !Type.isInstanceOf(targetValue, Type.Frame) )
            return new AError('#TARGET_IS_NOT_A_FRAME');

        const targetFrame = targetValue as IFrame;

        const resultOrError = await frameOp(targetFrame);
        if( Type.isInstanceOf(resultOrError, Type.Error) )
            return resultOrError as AError;

        return resultOrError as T;
    }

    //todo: move this to ISheetContext ?
    private async _setCellsValues(api:ISheetAPI, frame:IFrame, columnName:string, searchExpr:IFormulaExpression, newValue:IValue):Promise<IFrame | AError>
    {
        const namedArguments:NamedArguments = new Map<string, IValue | ISheetValue>();

        const fields = frame.getFields();
        const records = frame.getRecords();

        const rowArgName = !frame.findField('row') ? '@row' : '@_row';
        const colArgName = !frame.findField('column') ? '@column' : '@_column';

        const data = [];
        let changedCellsCnt = 0;
        for( let i = 0; i < records.length; ++i )
        {
            const record = records[i];
            const cells = record.getCells();

            fields.map((field, fieldIndex) => namedArguments.set(`@${field.name}`, cells[fieldIndex]));
            namedArguments.set(rowArgName, AInteger.fromNumber(i + 1)); //1-based

            let row = [];

            if( columnName )
            {
                // column name specified => evaluate on per-row basis, then just change this column
                const rowFits = await searchExpr.eval(namedArguments)
                    .then(er => Type.isInstanceOf(er, Type.Boolean) && (er as ABoolean).value);

                row = rowFits
                    ? cells.map((oldValue, colIndex) => fields[colIndex].name == columnName ? newValue : oldValue)
                    : cells;

                if( rowFits )
                    changedCellsCnt++;
            }
            else
            {
                // no column name => evaluate on per-cell basis
                for( let j = 0; j < fields.length; ++j )
                {
                    namedArguments.set(colArgName, AInteger.fromNumber(j + 1)); //1-based

                    const cellFits = await searchExpr.eval(namedArguments)
                        .then(er => Type.isInstanceOf(er, Type.Boolean) && (er as ABoolean).value);

                    row.push(cellFits ? newValue : cells[j]);

                    if( cellFits )
                        changedCellsCnt++;
                }
            }

            data.push(row);
        }

        if( changedCellsCnt === 0 )
            return new AError('#EXPR_MATCHES_NO_CELLS');

        return Frames.fromFieldsAndData(fields, data, frame.getMeta());
    }

    private async _searchFrameRowsByExprFormula(api:ISheetAPI, frame:IFrame, searchExpr:IFormulaExpression, resultCardinality:Cardinality):Promise<Array<number> | AError>
    {
        const sheetContext = api.getSheetContext();
        const evaluatorFn = (namedArguments:NamedArguments) => searchExpr.eval(namedArguments)
            .then((evalValue:IValue | ISheetValue) =>
            {
                const convertedValue = sheetContext.convertToIValue(evalValue);
                if( Array.isArray(convertedValue) )
                    return new AError('#INVALID_EVAL_EXPR');

                return convertedValue as IValue;
            });

        return this._searchFrameRowsByExpr(frame, resultCardinality, evaluatorFn);
    }

    private async _searchFrameRowsByExprString(api:ISheetAPI, frame:IFrame, searchExpr:string, resultCardinality:Cardinality):Promise<Array<number> | AError>
    {
        const evaluatorFn = (namedArguments:NamedArguments) =>
            api.getSheetContext().evalExpr(searchExpr, namedArguments, true);

        return this._searchFrameRowsByExpr(frame, resultCardinality, evaluatorFn);
    }

    private async _searchFrameRowsByExpr(frame:IFrame, resultCardinality:Cardinality, evaluatorFn:(NamedArguments) => Promise<IValue>):Promise<Array<number> | AError>
    {
        //possible errors:
        // #EXPR_MATCHES_NO_ROWS
        // returns this error if Expr matches 0 rows
        //
        // #EXPR_MATCHES_MULTIPLE_ROWS
        // returns this error if Expr matches > 1 rows

        const namedArguments:NamedArguments = new Map<string, IValue | ISheetValue>();

        const fields = frame.getFields();
        const records = frame.getRecords();
        const resultIndexes = [];

        const rowArgName = !frame.findField('row') ? '@row' : '@_row';

        for( let i = 0; i < records.length; ++i )
        {
            const record = records[i];
            const cells = record.getCells();

            fields.map((field, fieldIndex) => namedArguments.set(`@${field.name}`, cells[fieldIndex]));
            namedArguments.set(rowArgName, AInteger.fromNumber(i + 1)); //1-based

            const evalResult = await evaluatorFn(namedArguments);
            const rowFits = Type.isInstanceOf(evalResult, Type.Boolean) && (evalResult as ABoolean).value;

            if( rowFits )
            {
                resultIndexes.push(i);

                // too much rows => return Error
                if( resultIndexes.length > resultCardinality.maxRows )
                    return new AError('#EXPR_MATCHES_MULTIPLE_ROWS');
            }
        }

        if( resultIndexes.length < resultCardinality.minRows )
            return new AError('#EXPR_MATCHES_NO_ROWS');

        return resultIndexes;
    }

    private _searchFrameColumn(columnNameOrOffset:IValue, frame:IFrame):number | AError
    {
        const fieldCount = frame.fieldCount;
        let fieldIndex = -1;

        if( Type.isInstanceOf(columnNameOrOffset, Type.Number) ||
            Type.isInstanceOf(columnNameOrOffset, Type.Integer) ||
            Type.isInstanceOf(columnNameOrOffset, Type.Byte) )
        {
            // coerce to String and treat as a columnName
            const coerced = CoercionUtils.coerce(columnNameOrOffset, Type.Integer, null);
            if( Type.isInstanceOf(coerced, Type.Error) )
                return new AError('#COERCION_ERROR');

            // columns are 1-based from perspective of a user => so we need to substract 1
            fieldIndex = (coerced as AInteger).value - 1;
        }
        else
        {
            // coerce to String and treat as a columnName
            const coerced = CoercionUtils.coerce(columnNameOrOffset, Type.String, null);
            if( Type.isInstanceOf(coerced, Type.Error) )
                return new AError('#COERCION_ERROR');

            const columnName = (coerced as AString).value;
            fieldIndex = frame.getFields()
                .findIndex(f => f.name.toLowerCase() === columnName.toLowerCase());
        }

        return fieldIndex >= 0 && fieldIndex < fieldCount
            ? fieldIndex
            : new AError('#COLUMN_NOT_FOUND');
    }

    private _makeFrame(data:FrameData, hasHeaders:boolean):IFrame | AError
    {
        if( data.length === 0 )
            return Frames.empty();

        const namingContext = new SimpleNamingContext();

        const fields = [];
        if( hasHeaders )
        {
            const fieldValues = data.shift();

            const fieldsMap = {};
            for( let fieldValue of fieldValues )
            {
                let fieldName;
                if( Type.isInstanceOf(fieldValue, Type.Frame) || BaseValue.isValueEmpty(fieldValue) )
                    fieldName = namingContext.getNextColumnName();
                else
                {
                    const coerced = CoercionUtils.coerce(fieldValue, Type.String, null);
                    if( Type.isInstanceOf(coerced, Type.Error) )
                        return new AError('#INVALID_VALUE_FOR_COLUMN_NAME');

                    fieldName = (coerced as AString).value;
                }

                if( fieldsMap[fieldName] )
                    return new AError('#NON_UNIQUE_COLUMN_NAMES');

                fieldsMap[fieldName] = true;
                fields.push(new FrameField(fieldName, Type.String));
            }
        }
        else
        {
            const fieldCount = data[0].length;
            for( let i = 0; i < fieldCount; ++i )
                fields.push(new FrameField(namingContext.getNextColumnName(), Type.String));
        }

        return Frames.fromFieldsAndData(fields, data);
    }

    private _getArgumentAsFrame(api:ISheetAPI, value:any, autoRows:boolean, autoCols:boolean):IFrame | AError
    {
        if( Type.isInstanceOf(value, Type.Frame) )
            return value as IFrame;
        else if( SheetValueType.isInstanceOf(value, SheetType.SheetRange) )
            return api.getSheetContext().getRangeValue(value as ISheetRange, autoRows, autoCols);
        else if( SheetValueType.isInstanceOf(value, SheetType.SheetLocation) )
        {
            const valueAtLocation = api.getSheetContext().getCellValue(value as ISheetLocation);
            if( !Type.isInstanceOf(valueAtLocation, Type.Frame) )
                return new AError('#INVALID_ARGUMENT');

            return valueAtLocation as IFrame;
        }

        return new AError('#INVALID_ARGUMENT');
    }

    private _getFrameFields(frame:IFrame):FrameFieldsObj
    {
        let frameFields = {};

        frame.getFieldNames().forEach((fieldName:FieldName) => frameFields[fieldName.toLowerCase()] = true);

        return frameFields;
    }
}
